import { useCallback, useEffect, useState } from "react";
import {
  ALL_NOTIFIABLE_ALERT_TYPES,
  LEVEL_ORDER,
  levelOf,
} from "@/constants/alertConfig.js";
import { IDENTIFY_MODES } from "@/lib/alertSpeech.js";
import { isNative } from "@/lib/platform.js";

/**
 * useVoicePreferences — preferencias de la locución por voz, en localStorage.
 *
 * Van en localStorage y NO en el servidor a propósito: son preferencias del
 * PUESTO, no del usuario. El mismo operador quiere voz en el monitor del
 * despacho y silencio en el celular que carga en el bolsillo, y una preferencia
 * sincronizada le impondría una sola respuesta a los dos.
 *
 * Misma mecánica de sincronización que `useMapPreferences`: evento `storage`
 * para otras pestañas + evento propio para instancias hermanas de esta misma.
 */

// La clave sube de versión en cada cambio de significado, para que la migración
// corra una sola vez y quede escrita; leer la vieja y no persistir dejaría la
// conversión repitiéndose en cada arranque y pisaría cambios hechos desde otra
// pestaña.
//
//   v2 → `minLevel` (un umbral por severidad) pasó a ser `types` (opt-in por
//        tipo de alerta).
//   v3 → `enabled` pasó a gobernar TAMBIÉN la locución con la app cerrada. Ver
//        `migrateToV3`: el valor guardado en v2 no se puede heredar tal cual.
//   v4 → la escala de `rate` bajó un escalón. Ver `migrateToV4`.
const STORAGE_KEY = "voice.announcements.v4";
// De la más nueva a la más vieja: se lee la primera que exista, con las
// migraciones que le falten para llegar a la versión actual.
const LEGACY_STORAGE_KEYS = [
  { key: "voice.announcements.v3", migrate: (p) => migrateToV4(p) },
  { key: "voice.announcements.v2", migrate: (p) => migrateToV4(migrateToV3(p)) },
  { key: "voice.announcements.v1", migrate: (p) => migrateToV4(migrateToV3(p)) },
];
const SYNC_EVENT = "voice-prefs-sync";

/**
 * Mapa tipo → ¿se locuta?, derivado de un umbral por severidad. Es la regla de
 * migración desde v1 y también la que arma el default de fábrica: así lo que se
 * escucha hoy no cambia hasta que el usuario toque la lista.
 */
function typesFromMinLevel(minLevel) {
  const umbral = LEVEL_ORDER.indexOf(minLevel);
  const map = {};
  for (const type of ALL_NOTIFIABLE_ALERT_TYPES) {
    map[type] = LEVEL_ORDER.indexOf(levelOf(type)) >= umbral;
  }
  return map;
}

export const DEFAULT_VOICE_PREFS = Object.freeze({
  // Apagado por defecto EN LA WEB: una pestaña que empieza a hablar sola sin
  // que nadie lo pidiera es un susto, no una feature. Además la política de
  // autoplay exige un gesto del usuario para desbloquear el audio — el toggle
  // ES ese gesto. En la app el default se invierte, ver `platformDefaults`.
  enabled: false,
  // Por tipo de alerta. El default equivale al viejo umbral HIGH: en INFO entran
  // ENGINE_ON/OFF, que en una flota activa son cientos por día y convertían la
  // voz en ruido de fondo.
  types: Object.freeze(typesFromMinLevel("HIGH")),
  volume: 0.7,
  // "Normal" en la tarjeta. Mismo default que `VoicePreferences.rate` del lado
  // nativo, que es el que habla con la app cerrada.
  rate: 0.9,
  earcons: true,
  voiceURI: null,
  // A quién se nombra en la locución: "plate" | "driver" | "both".
  // La placa es el default porque es el único identificador que SIEMPRE existe:
  // un vehículo sin conductor asignado es normal (taller, disponible, recién
  // dado de alta) y no debe quedarse sin anunciar.
  identify: "plate",
  // Deletreo fonético de la placa ("Xilófono Yate Zapato cero uno Hora").
  // Apagado por defecto: triplica el largo de la locución, y con buen audio la
  // placa se entiende sin deletrear. Se enciende para radio, moto o taller.
  phonetic: false,
});

/**
 * Los defaults de fábrica de ESTE puesto.
 *
 * En la app el switch de voz arranca ENCENDIDO, y los dos motivos del apagado
 * de la web no aplican ahí: no hay política de autoplay que exija un gesto
 * previo (es una app, no una pestaña — `primeSpeech` ni intenta cebar nada), y
 * una app que se instala PARA que el teléfono hable de madrugada no puede
 * llegar muda. Desde que el switch también gobierna la locución con la app
 * cerrada, dejarlo en `false` de fábrica sería apagar la razón de ser de la app.
 */
function platformDefaults() {
  if (!isNative()) return DEFAULT_VOICE_PREFS;
  return { ...DEFAULT_VOICE_PREFS, enabled: true };
}

/**
 * Migración v2 → v3: en la app, `enabled` no se hereda.
 *
 * En v2 este switch gobernaba SOLO la locución de esta pestaña; con la app
 * cerrada el teléfono hablaba pasara lo que pasara. O sea que un `false`
 * guardado en v2 no puede significar "no quiero que el teléfono hable con la
 * app cerrada" — nadie tuvo la posibilidad de elegir eso.
 *
 * Y heredarlo sería peor que impreciso: el default de fábrica era apagado y se
 * persistía en el primer render aunque el usuario no tocara nada, así que
 * CUALQUIER instalación que nunca abrió la tarjeta tiene un `false` guardado.
 * Heredarlo dejaría muda de golpe a toda la flota. `true` conserva el
 * comportamiento que estaba realmente en efecto.
 *
 * En web no se toca: ahí el `false` sí quería decir lo que dice.
 */
function migrateToV3(parsed) {
  if (!isNative() || !parsed || typeof parsed !== "object") return parsed;
  return { ...parsed, enabled: true };
}

/**
 * Escala vieja → escala nueva, por ETIQUETA: quien tenía "Normal" sigue en
 * "Normal", que ahora es más lenta. La escala bajó entera porque la "Normal"
 * de antes se oía apurada en el teléfono.
 *
 * La vieja "Pausada" (0.9) no figura porque no se traduce: 0.9 es la "Normal"
 * de hoy, que es justo lo que se pidió — lo que antes era pausado pasa a ser
 * lo normal. Ningún valor nuevo coincide con uno viejo que se traduzca, así que
 * releer un v4 nunca vuelve a mover nada.
 */
const LEGACY_RATES = { 1.05: 0.9, 1.3: 1.1 };

/** Migración v3 → v4: la escala de velocidad bajó un escalón. */
function migrateToV4(parsed) {
  if (!parsed || typeof parsed !== "object") return parsed;
  const nueva = LEGACY_RATES[parsed.rate];
  return nueva === undefined ? parsed : { ...parsed, rate: nueva };
}

/**
 * Normaliza el mapa de tipos, con la migración de v1 adentro.
 *
 * Se guardan SOLO los tipos conocidos por este build: uno desconocido guardado
 * por una versión futura no debe quedar pegado acá, y uno nuevo que todavía no
 * está en el mapa cae al default de `isTypeAnnounced` en vez de al silencio.
 */
function sanitizeTypes(parsed) {
  const guardado = parsed?.types;
  if (guardado && typeof guardado === "object") {
    const map = {};
    for (const type of ALL_NOTIFIABLE_ALERT_TYPES) {
      if (typeof guardado[type] === "boolean") map[type] = guardado[type];
    }
    return map;
  }
  // v1: un único umbral por severidad. Se convierte al equivalente por tipo.
  if (LEVEL_ORDER.includes(parsed?.minLevel)) return typesFromMinLevel(parsed.minLevel);
  return { ...DEFAULT_VOICE_PREFS.types };
}

/**
 * ¿Se locuta este tipo de alerta?
 *
 * El default para un tipo ausente del mapa NO es el silencio: un tipo nuevo que
 * este build todavía no conoce llega igual por el WebSocket, y dejarlo mudo
 * significaría que el operador no se entera de un evento que el backend sí
 * consideró digno de alerta. Mismo criterio que `UNKNOWN_ALERT_LEVEL`.
 */
export function isTypeAnnounced(prefs, alertType) {
  const explicito = prefs?.types?.[alertType];
  if (typeof explicito === "boolean") return explicito;
  return levelOf(alertType) !== "INFO";
}

function sanitize(parsed) {
  const defaults = platformDefaults();
  if (!parsed || typeof parsed !== "object") return defaults;
  const num = (value, fallback, min, max) =>
    typeof value === "number" && Number.isFinite(value)
      ? Math.min(max, Math.max(min, value))
      : fallback;

  return {
    enabled: typeof parsed.enabled === "boolean" ? parsed.enabled : defaults.enabled,
    types: sanitizeTypes(parsed),
    volume: num(parsed.volume, DEFAULT_VOICE_PREFS.volume, 0, 1),
    rate: num(parsed.rate, DEFAULT_VOICE_PREFS.rate, 0.5, 2),
    earcons: typeof parsed.earcons === "boolean" ? parsed.earcons : DEFAULT_VOICE_PREFS.earcons,
    phonetic:
      typeof parsed.phonetic === "boolean" ? parsed.phonetic : DEFAULT_VOICE_PREFS.phonetic,
    // Un valor fuera del catálogo (build viejo, storage editado a mano) cae al
    // default en vez de silenciar al sujeto de la frase.
    identify: IDENTIFY_MODES.includes(parsed.identify)
      ? parsed.identify
      : DEFAULT_VOICE_PREFS.identify,
    // La voz elegida puede desaparecer (otro equipo, otro navegador). No se
    // valida contra el catálogo acá porque en el primer render todavía está
    // vacío; `speakText` cae sola a la voz por defecto si el URI no existe.
    voiceURI: typeof parsed.voiceURI === "string" ? parsed.voiceURI : null,
  };
}

function readFromStorage() {
  if (typeof window === "undefined") return platformDefaults();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return sanitize(JSON.parse(raw));

    // Las claves viejas solo se miran si todavía no hay una de esta versión. El
    // primer render persiste el resultado migrado, así que la conversión ocurre
    // una única vez.
    for (const { key, migrate } of LEGACY_STORAGE_KEYS) {
      const legacy = window.localStorage.getItem(key);
      if (legacy) return sanitize(migrate(JSON.parse(legacy)));
    }
    return platformDefaults();
  } catch {
    return platformDefaults();
  }
}

function typesEqual(a = {}, b = {}) {
  const claves = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of claves) if (a[k] !== b[k]) return false;
  return true;
}

function prefsEqual(a, b) {
  return (
    a.enabled === b.enabled &&
    typesEqual(a.types, b.types) &&
    a.volume === b.volume &&
    a.rate === b.rate &&
    a.earcons === b.earcons &&
    a.phonetic === b.phonetic &&
    a.identify === b.identify &&
    a.voiceURI === b.voiceURI
  );
}

function writeToStorage(prefs) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    // Las claves viejas se borran DESPUÉS de que la nueva quedó escrita: dejarlas
    // ahí era guardar una foto de hace meses lista para revivir. Si algún día la
    // clave actual se pierde —desalojo por cuota, una limpieza a medias—,
    // `readFromStorage` caía en la vieja y re-migraba, revirtiendo en silencio
    // todo lo configurado desde entonces.
    for (const { key } of LEGACY_STORAGE_KEYS) window.localStorage.removeItem(key);
    window.dispatchEvent(new CustomEvent(SYNC_EVENT));
  } catch {
    /* quota / disabled — ignore */
  }
}

export function useVoicePreferences() {
  const [prefs, setPrefs] = useState(readFromStorage);

  useEffect(() => {
    writeToStorage(prefs);
  }, [prefs]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const refresh = () => {
      const next = readFromStorage();
      setPrefs((prev) => (prefsEqual(prev, next) ? prev : next));
    };
    const onStorage = (e) => {
      if (e.key && e.key !== STORAGE_KEY) return;
      refresh();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener(SYNC_EVENT, refresh);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SYNC_EVENT, refresh);
    };
  }, []);

  const update = useCallback((patch) => {
    setPrefs((prev) => sanitize({ ...prev, ...patch }));
  }, []);

  return { prefs, update };
}
